import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, StatusPill, Spinner, PageSkeleton } from '@daycal/ui';
import { formatters } from '@daycal/shared';
import { api } from '../lib/apiClient.js';
import { colorOf } from '../lib/bookingColors.js';
import {
  FiCalendar, FiChevronLeft, FiChevronRight, FiEye, FiX, FiMail, FiPhone,
  FiGlobe, FiMapPin, FiVideo, FiDollarSign, FiTag, FiHelpCircle,
} from 'react-icons/fi';

function MonthPicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [year, setYear] = useState(() => value ? parseInt(value.split('-')[0]) : new Date().getFullYear());
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref]);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const selectedYear = value ? parseInt(value.split('-')[0]) : null;
  const selectedMonth = value ? parseInt(value.split('-')[1]) : null;

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between py-2 px-3 w-40 rounded-lg text-sm shadow-sm cursor-pointer border border-slate-200 bg-white text-slate-900 transition-colors hover:border-slate-300"
      >
        <span className="font-medium text-slate-700">{value ? `${months[selectedMonth - 1]} ${selectedYear}` : 'All time'}</span>
        <FiCalendar className="text-slate-400" />
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 sm:left-0 z-50 w-64 rounded-2xl bg-white p-4 shadow-[0_20px_50px_rgba(0,0,0,0.25)] border border-slate-200 animate-dc-rise">
           <div className="flex items-center justify-between mb-4">
             <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600 transition-colors">
               <FiChevronLeft className="w-4 h-4" />
             </button>
             <span className="font-bold text-slate-900">{year}</span>
             <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600 transition-colors">
               <FiChevronRight className="w-4 h-4" />
             </button>
           </div>
           <div className="grid grid-cols-3 gap-1.5">
             {months.map((m, i) => {
               const isSelected = selectedYear === year && selectedMonth === i + 1;
               return (
                 <button
                   key={m}
                   onClick={() => {
                     onChange(`${year}-${String(i + 1).padStart(2, '0')}`);
                     setIsOpen(false);
                   }}
                   className={`rounded-lg py-2 text-sm font-semibold transition-colors ${
                     isSelected
                       ? 'bg-slate-900 text-white shadow-sm'
                       : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                   }`}
                 >
                   {m}
                 </button>
               );
             })}
           </div>
           <div className="mt-4 border-t border-slate-100 pt-3">
             <button
               onClick={() => { onChange(''); setIsOpen(false); }}
               className="w-full rounded-lg py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
             >
               Clear filter
             </button>
           </div>
        </div>
      )}
    </div>
  )
}

const LOCATION_META = {
  online: { icon: FiVideo, label: 'Online meeting' },
  phone: { icon: FiPhone, label: 'Phone call' },
  in_person: { icon: FiMapPin, label: 'In person' },
  custom: { icon: FiMapPin, label: 'Custom' },
};

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-sm text-slate-900 break-words">{children}</div>
      </div>
    </div>
  );
}

// Read-only detail view for a single booking: invitee contact, event/schedule,
// location, payment, and the invitee's custom form answers (fetched lazily on open).
export function BookingDetailsModal({ bookingId, onClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => api.getBooking(bookingId),
    enabled: !!bookingId,
  });

  const b = data?.booking;
  const tz = b?.invitee_timezone || 'Asia/Kolkata';
  const answers = Array.isArray(b?.answers) ? b.answers.filter((a) => a.value != null && a.value !== '') : [];
  const loc = b ? (LOCATION_META[b.location_type] || LOCATION_META.custom) : null;
  const paid = b?.paid_amount_minor ?? b?.original_price_minor;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl animate-dc-rise"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{b?.invitee_name || 'Booking details'}</h3>
            {b?.event_title && <p className="text-sm text-slate-500">{b.event_title}</p>}
          </div>
          <div className="flex items-center gap-3">
            {b && <StatusPill status={b.status} />}
            <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {isLoading && <div className="flex justify-center py-10"><Spinner /></div>}
          {isError && <p className="py-8 text-center text-sm text-red-500">Couldn't load booking details.</p>}

          {b && (
            <div className="space-y-5">
              <DetailRow icon={FiCalendar} label="When">
                {formatters.formatDateTime(b.start_utc, tz)}
                {b.duration_min ? <span className="text-slate-500"> · {b.duration_min} min</span> : null}
              </DetailRow>

              <DetailRow icon={FiMail} label="Email">
                {b.invitee_email
                  ? <a href={`mailto:${b.invitee_email}`} className="text-slate-900 underline decoration-slate-300 hover:decoration-slate-900">{b.invitee_email}</a>
                  : <span className="text-slate-400">Not provided</span>}
              </DetailRow>

              <DetailRow icon={FiGlobe} label="Timezone">
                {b.invitee_timezone || <span className="text-slate-400">Not provided</span>}
              </DetailRow>

              {loc && (
                <DetailRow icon={loc.icon} label="Location">
                  {loc.label}
                  {b.meet_url && (
                    <> · <a href={b.meet_url} target="_blank" rel="noreferrer" className="underline decoration-slate-300 hover:decoration-slate-900">Join link</a></>
                  )}
                </DetailRow>
              )}

              {paid != null && (
                <DetailRow icon={FiDollarSign} label="Payment">
                  {formatters.formatMoney(paid, b.currency || 'INR')}
                  {b.promo_code_id && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600"><FiTag className="h-3 w-3" /> Promo applied</span>}
                </DetailRow>
              )}

              {answers.length > 0 && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <FiHelpCircle className="h-3.5 w-3.5" /> Form responses
                  </div>
                  <div className="space-y-3">
                    {answers.map((a, i) => (
                      <div key={i} className="rounded-xl bg-slate-50 px-4 py-3">
                        <div className="text-xs font-medium text-slate-500">{a.label}</div>
                        <div className="mt-0.5 text-sm text-slate-900 break-words whitespace-pre-wrap">{a.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {answers.length === 0 && !isLoading && (
                <p className="border-t border-slate-100 pt-4 text-sm text-slate-400">No additional form responses.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Host booking management (architecture.md §24 M9): list + cancel/complete.
// When navigated to from the Booking Calendar page (location.state.highlightId),
// the target booking's card is scrolled into view and flashed with a coloured ring.
export default function Bookings() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['bookings'], queryFn: () => api.listBookings() });

  const [confirmAction, setConfirmAction] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [statusTab, setStatusTab] = useState('all');
  const [highlightedId, setHighlightedId] = useState(null);
  const hlTimer = useRef(null);
  const handledHighlight = useRef(null);

  useEffect(() => () => clearTimeout(hlTimer.current), []);

  const cancel = useMutation({
    mutationFn: (id) => api.cancelBooking(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      setConfirmAction(null);
    },
  });
  const complete = useMutation({
    mutationFn: (id) => api.completeBooking(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      setConfirmAction(null);
    },
  });

  // Scroll a booking card into view and flash a status-coloured highlight ring.
  const focusBooking = (id, startUtc) => {
    if (filterMonth && startUtc && !startUtc.startsWith(filterMonth)) setFilterMonth('');
    setHighlightedId(id);
    setTimeout(() => {
      document.getElementById(`booking-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    clearTimeout(hlTimer.current);
    hlTimer.current = setTimeout(() => setHighlightedId(null), 3000);
  };

  // Arriving from the Booking Calendar page: highlight the clicked booking once
  // data is loaded, then clear the router state so a refresh won't re-trigger it.
  useEffect(() => {
    const hid = location.state?.highlightId;
    if (!hid || isLoading) return;
    if (handledHighlight.current === hid) return;
    handledHighlight.current = hid;
    focusBooking(hid, location.state?.startUtc);
    navigate('.', { replace: true, state: {} });
  }, [location.state, isLoading]);

  if (isLoading) return <PageSkeleton />;
  const bookings = data?.bookings || [];

  const filteredBookings = bookings.filter((b) => {
    const matchesMonth = !filterMonth || b.start_utc.startsWith(filterMonth);
    const matchesStatus = statusTab === 'all' || b.status === statusTab;
    return matchesMonth && matchesStatus;
  });

  const statusCounts = {
    all: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'cancel') cancel.mutate(confirmAction.id);
    else if (confirmAction.type === 'complete') complete.mutate(confirmAction.id);
  };

  const getAccentBorder = (status) => {
    switch (status) {
      case 'confirmed': return 'border-l-4 border-l-emerald-500';
      case 'completed': return 'border-l-4 border-l-brand-600';
      case 'cancelled': return 'border-l-4 border-l-rose-500';
      default: return 'border-l-4 border-l-slate-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Month Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Bookings & Appointments</h1>
            <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-bold text-white shadow-xs">
              {bookings.length}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Manage attendees, view booking answers, and update status in real-time.</p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <MonthPicker value={filterMonth} onChange={setFilterMonth} />
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
        {[
          { key: 'all', label: 'All Bookings', count: statusCounts.all },
          { key: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed },
          { key: 'completed', label: 'Completed', count: statusCounts.completed },
          { key: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
        ].map((tab) => {
          const isActive = statusTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusTab(tab.key)}
              className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 shrink-0 cursor-pointer ${
                isActive
                  ? 'bg-slate-900 text-white shadow-md shadow-slate-900/10'
                  : 'bg-white border border-slate-200/80 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                isActive ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Booking Cards Grid */}
      <div className="grid gap-3.5">
        {filteredBookings.map((b) => (
          <div
            key={b.id}
            id={`booking-${b.id}`}
            className={`scroll-mt-6 rounded-dc border border-slate-200/80 bg-white p-5 shadow-xs transition-all duration-300 hover:shadow-md ${getAccentBorder(b.status)} ${
              highlightedId === b.id ? `ring-2 ring-offset-2 shadow-lg ${colorOf(b.status).ring}` : ''
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              {/* Left Side (Avatar, Invitee Name & Date/Time) */}
              <div className="flex items-start gap-3.5">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-700 text-xs font-bold text-white shadow-xs">
                  {(b.invitee_name || 'A').charAt(0).toUpperCase()}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-slate-900 text-base">{b.invitee_name || '(anonymized)'}</span>
                    {b.event_title && (
                      <span className="hidden md:inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 border border-slate-200/60">
                        {b.event_title}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                    <span className="inline-flex items-center gap-1.5 font-medium text-slate-700">
                      <FiCalendar className="h-3.5 w-3.5 text-brand-600" />
                      {formatters.formatDateTime(b.start_utc, b.invitee_timezone || 'Asia/Kolkata')}
                    </span>
                    {b.duration_min && (
                      <span className="text-slate-400">· {b.duration_min} mins</span>
                    )}
                  </div>

                  <div className="mt-2 sm:hidden">
                    <StatusPill status={b.status} />
                  </div>
                </div>
              </div>

              {/* Right Side (Status Pill + Actions) */}
              <div className="flex flex-wrap items-center gap-3 self-end sm:self-center shrink-0">
                <div className="hidden sm:block">
                  <StatusPill status={b.status} />
                </div>

                {b.meet_url && (
                  <a
                    href={b.meet_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Join video call"
                    title="Join video call"
                    className="grid h-9 w-9 place-items-center rounded-full border border-indigo-200 bg-indigo-50 text-indigo-600 shadow-xs transition-all hover:bg-indigo-600 hover:text-white hover:border-indigo-600 active:scale-95 cursor-pointer"
                  >
                    <FiVideo className="h-4 w-4" />
                  </a>
                )}

                <button
                  onClick={() => setDetailId(b.id)}
                  aria-label="View booking details"
                  title="View full details"
                  className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-xs transition-all hover:bg-slate-800 hover:text-white hover:border-slate-800 active:scale-95 cursor-pointer"
                >
                  <FiEye className="h-4 w-4" />
                </button>

                {b.status === 'confirmed' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmAction({ type: 'complete', id: b.id })}
                      className="rounded-dc-sm bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-xs transition-all hover:bg-emerald-700 active:scale-95 cursor-pointer"
                    >
                      Complete
                    </button>
                    <button
                      onClick={() => setConfirmAction({ type: 'cancel', id: b.id })}
                      className="rounded-dc-sm bg-rose-50 border border-rose-200/80 px-4 py-2 text-xs font-semibold text-rose-600 transition-all hover:bg-rose-600 hover:text-white active:scale-95 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredBookings.length === 0 && (
          <div className="rounded-dc border border-dashed border-slate-200/80 bg-white p-12 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400 mb-3">
              <FiCalendar className="h-6 w-6" />
            </div>
            <p className="font-bold text-slate-800 text-sm">No bookings found</p>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              {bookings.length > 0
                ? "There are no bookings matching the selected status or month filter."
                : "You don't have any bookings yet. Share your booking link to start scheduling meetings!"}
            </p>
          </div>
        )}
      </div>

      {detailId && <BookingDetailsModal bookingId={detailId} onClose={() => setDetailId(null)} />}

      {confirmAction && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs transition-opacity">
          <div className="w-full max-w-sm rounded-dc bg-white p-6 shadow-2xl animate-dc-rise border border-slate-200/80">
            <h3 className="font-display text-lg font-bold text-slate-900">Are you sure?</h3>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Are you sure you want to <span className="font-bold text-slate-900 uppercase">{confirmAction.type}</span> this appointment?
            </p>
            <div className="mt-6 flex justify-end gap-2.5">
              <button 
                onClick={() => setConfirmAction(null)} 
                className="rounded-dc-sm bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={cancel.isPending || complete.isPending}
                className={`rounded-dc-sm px-4 py-2 text-xs font-semibold text-white shadow-xs transition-all active:scale-95 ${
                  confirmAction.type === 'cancel' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {cancel.isPending || complete.isPending ? <Spinner className="!h-3.5 !w-3.5 !border-t-white" /> : `Confirm ${confirmAction.type}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
